import Papa from "papaparse";
import { importOutreachData } from "../services/outreach/firestore/import";
import {
  ReactionChannel,
  PlaylistingChannel,
  SpotifyPlaylist,
  MusicBlog,
  OutreachCategory,
} from "../types/outreach";

// Define a type that excludes CAMPAIGNS and MESSAGE_CORRELATIONS from OutreachCategory
type ImportableOutreachCategory = Exclude<
  OutreachCategory,
  OutreachCategory.CAMPAIGNS | OutreachCategory.MESSAGE_CORRELATIONS
>;

// Expected CSV column headers for each importable category
const expectedHeaders: Record<ImportableOutreachCategory, string[]> = {
  [OutreachCategory.REACTION_CHANNELS]: [
    "Name of Channel",
    "# of Subs",
    "Genre",
    "Genre",
    "Email/Contact",
    "Social Handles",
  ],
  [OutreachCategory.PLAYLISTING_CHANNELS]: [
    "Name of Channel",
    "# of Subs",
    "Genre",
    "Genre",
    "Email/Contact",
  ],
  [OutreachCategory.SPOTIFY_PLAYLISTS]: [
    "Artist",
    "Playlist URL",
    "Curator Social Handle",
    "Source",
    "Genre",
    "Curator Email",
    "Playlist Likes",
    "Playlist Followers Count",
  ],
  [OutreachCategory.MUSIC_BLOGS]: [
    "Blogs",
    "Contact",
    "Link",
    "Total Monthly Visits",
  ],
  [OutreachCategory.MUSIC_INDUSTRY]: [], // Empty array since we're removing this tab
};

// Validate CSV headers against expected headers
const validateHeaders = (
  headers: string[],
  category: ImportableOutreachCategory
): { isValid: boolean; missingColumns: string[] } => {
  const expected = expectedHeaders[category];

  // If we don't have expected headers for this category, return invalid
  if (!expected) {
    return { isValid: false, missingColumns: [] };
  }

  // Check if all expected headers are present in the CSV
  const missingColumns = expected.filter(
    (header: string) => !headers.includes(header)
  );

  return {
    isValid: missingColumns.length === 0,
    missingColumns,
  };
};

// Parse CSV data for Reaction Channels
const parseReactionChannels = (data: any[]): Omit<ReactionChannel, "id">[] => {
  return data.map((row) => ({
    channelName: row["Name of Channel"] || "",
    channelUrl: "", // No URL in CSV, would need to be added manually
    subscriberCount: row["# of Subs"] || "",
    genre: row["Genre"] || "Any", // Using the first Genre column
    email: row["Email/Contact"] || "",
    socialHandles: row["Social Handles"] || "",
  }));
};

// Parse CSV data for Playlisting Channels
const parsePlaylistingChannels = (
  data: any[]
): Omit<PlaylistingChannel, "id">[] => {
  return data.map((row) => ({
    channelName: row["Name of Channel"] || "",
    channelUrl: "", // No URL in CSV, would need to be added manually
    subscriberCount: row["# of Subs"] || "",
    genre: row["Genre"] || "Any", // Using the first Genre column
    email: row["Email/Contact"] || "",
  }));
};

// Parse CSV data for Spotify Playlists
const parseSpotifyPlaylists = (data: any[]): Omit<SpotifyPlaylist, "id">[] => {
  return data.map((row) => ({
    playlistName: row["Artist"] || "", // Using Artist as playlist name
    playlistUrl: row["Playlist URL"] || "",
    curatorSocialHandle: row["Curator Social Handle"] || "",
    source: row["Source"] || "",
    genre: row["Genre"] || "Any",
    curatorEmail: row["Curator Email"] || "",
    playlistLikes: parseInt(row["Playlist Likes"]) || 0,
    playlistFollowers: parseInt(row["Playlist Followers Count"]) || 0,
  }));
};

// Parse CSV data for Music Blogs
const parseMusicBlogs = (data: any[]): Omit<MusicBlog, "id">[] => {
  return data.map((row) => ({
    blogName: row["Blogs"] || "",
    contact: row["Contact"] || "",
    blogUrl: row["Link"] || "",
    genre: "Any", // Default genre since it's not in the CSV
    monthlyVisits: parseInt(row["Total Monthly Visits"]) || 0,
  }));
};

// Main function to import CSV data
export const importFromCsv = async (
  file: File,
  category: ImportableOutreachCategory
): Promise<void> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const { data, meta } = results;

          // Validate headers
          const { isValid, missingColumns } = validateHeaders(
            meta.fields || [],
            category
          );

          if (!isValid) {
            throw new Error(
              `CSV columns do not match expected format. Missing columns: ${missingColumns.join(
                ", "
              )}`
            );
          }

          // Parse data based on category
          let reactionChannels: Omit<ReactionChannel, "id">[] = [];
          let playlistingChannels: Omit<PlaylistingChannel, "id">[] = [];
          let spotifyPlaylists: Omit<SpotifyPlaylist, "id">[] = [];
          let musicBlogs: Omit<MusicBlog, "id">[] = [];

          switch (category) {
            case OutreachCategory.REACTION_CHANNELS:
              reactionChannels = parseReactionChannels(data as any[]);
              break;
            case OutreachCategory.PLAYLISTING_CHANNELS:
              playlistingChannels = parsePlaylistingChannels(data as any[]);
              break;
            case OutreachCategory.SPOTIFY_PLAYLISTS:
              spotifyPlaylists = parseSpotifyPlaylists(data as any[]);
              break;
            case OutreachCategory.MUSIC_BLOGS:
              musicBlogs = parseMusicBlogs(data as any[]);
              break;
            default:
              throw new Error(`Unsupported category: ${category}`);
          }

          // Import data to Firebase
          await importOutreachData(
            reactionChannels,
            playlistingChannels,
            spotifyPlaylists,
            musicBlogs,
            [] // Empty array for Music Industry since we're removing it
          );

          resolve();
        } catch (error) {
          reject(error);
        }
      },
      error: (error) => {
        reject(new Error(`Error parsing CSV: ${error.message}`));
      },
    });
  });
};

// Function to generate CSV template
export const generateCsvTemplate = (
  category: ImportableOutreachCategory
): string => {
  const headers = expectedHeaders[category];
  if (!headers) {
    return ""; // Return empty string if no headers are defined for the category
  }
  return headers.join(",") + "\n"; // CSV string with headers
};
